import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useFormik } from "formik";
import { Link } from "react_reusable_components";
import { ArrowLeftOutlined } from "@ant-design/icons";

import Button from "../../components/Button";
import { TwoRow } from "../../core/layouts";

import {
  forgotPasswordFieldNames,
  forgotPasswordInitialValues,
  forgotPasswordValidationSchema,
} from "../../validationSchema";
import variables from "../../themes/base/styles/variables";
import { AuthService } from "../../services";
import { encrypt, getLocalizedMessage } from "../../UtilityFunctions/utils";
import SetPassword from "../../components/SetPassword/SetPassword";
import PasswordChangedModal from "../PasswordChangedModal/PasswordChangedModal";
import { showNotification } from "../../UtilityFunctions/notification";
import { WEAK_PASSWORD } from "../../constants/errorConstants";

const ForgotPasswordResetStyle = {
  sectionsStyle: { marginBottom: variables.marginLarge },
  backToLogin: {
    margin: `${variables.marginMediumLarge} 0`,
    fontSize: variables.fontSizeNormal,
    lineHeight: variables.lineHeightLarge,
  },
  style: { position: "relative" },
};

const ForgotPasswordResetForm = () => {
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const navigate = useNavigate();
  const intl = useIntl();
  const { passwordResetKey } = useParams();

  const { values, handleBlur, handleChange, handleSubmit, errors, touched } =
    useFormik({
      initialValues: forgotPasswordInitialValues,
      onSubmit: async function (data) {
        setDisabled(true);
        AuthService.resetPassword({
          body: {
            password: encrypt(data[forgotPasswordFieldNames.PASSWORD]),
            passwordResetKey,
          },
          intl,
        })
          .then(() => {
            setDisabled(false);
            setOpen(true);
          })
          .catch((error) => {
            if (error?.response?.data?.code === WEAK_PASSWORD) {
              showNotification(
                "error",
                "Error",
                getLocalizedMessage(intl, "resetPassword.weakPassword"),
              );
            } else if (error?.response?.data?.code === "INVALID_RESET_PASSWORD_CODE") {
              navigate("/login/forgot-password");
            }
            setDisabled(false);
          });
      },
      validationSchema: forgotPasswordValidationSchema(intl),
    });

  function backToLogin() {
    navigate("/login");
  }

  return (
    <>
      <PasswordChangedModal open={open} />
      <TwoRow
        topSection={
          <SetPassword
            values={values}
            fieldNames={forgotPasswordFieldNames}
            errors={errors}
            handleChange={handleChange}
            handleBlur={handleBlur}
            labels={{
              [forgotPasswordFieldNames.PASSWORD]: getLocalizedMessage(
                intl,
                "login.label.newPassword",
              ),
              [forgotPasswordFieldNames.CONFIRM_PASSWORD]: getLocalizedMessage(
                intl,
                "login.label.confirmPassword",
              ),
            }}
            placeholders={{
              [forgotPasswordFieldNames.PASSWORD]: getLocalizedMessage(
                intl,
                "login.placeholder.newPassword",
              ),
              [forgotPasswordFieldNames.CONFIRM_PASSWORD]: getLocalizedMessage(
                intl,
                "login.placeholder.confirmPassword",
              ),
            }}
            touched={touched}
          />
        }
        bottomSection={
          <TwoRow
            topSection={
              <Button disabled={disabled} onClick={handleSubmit}>
                {getLocalizedMessage(intl, "button.submit")}
              </Button>
            }
            bottomSection={
              <Link
                type="secondary"
                href="#"
                style={ForgotPasswordResetStyle.backToLogin}
                onClick={backToLogin}
              >
                <ArrowLeftOutlined />{" "}
                {getLocalizedMessage(intl, "login.backToLogin")}
              </Link>
            }
            bottomSectionStyle={{
              justifyContent: "center",
            }}
          />
        }
        topSectionStyle={ForgotPasswordResetStyle.sectionsStyle}
        bottomSectionStyle={ForgotPasswordResetStyle.bottomSectionStyle}
      />
    </>
  );
};

export default ForgotPasswordResetForm;
